import { Injectable } from '@angular/core';
import { IColor, ProductDetail } from '../product-detail-dialog/product-detail.model';
import { BehaviorSubject, forkJoin, map, Observable, of, tap } from 'rxjs';
import { ProductDetailService } from '../product-detail-dialog/product-detail-service';
import { ItemService } from '../../entities/item/item-list/item.service';
import { AccountService } from '../../login/account.service';

export interface CartItem {
  id: string
  productId: number
  productImage: string
  name: string
  description?: string
  dimensions: string
  thickness?: number
  color?: IColor
  texture?: string
  price: number
  faceOne?: boolean
  faceTwo?: boolean
  platformCount: number
  halfPlatformCount: number
  quarterPlatformCount: number
  eighthPlatformCount: number
  quantity: number
  leadTime?: number
}


@Injectable({
  providedIn: 'root'
})
export class ProductCartService {
  private CART_KEY = 'my_cart_items';

  private cartItemsSubject = new BehaviorSubject<CartItem[]>(
    this.loadFromLocalStorage()
  );

  cartItems$ = this.cartItemsSubject.asObservable();

  constructor(private productDetailService: ProductDetailService, private itemService: ItemService, private accountService: AccountService) { }

  public getPriceJDE(itemIds: string[], customerAN: string) {
    return this.itemService.getPriceJDE(itemIds, customerAN);
  }

  private loadFromLocalStorage(): CartItem[] {
    const data = localStorage.getItem(this.CART_KEY);
    return data ? JSON.parse(data) : [];
  }

  private saveToLocalStorage(items: CartItem[]) {
    localStorage.setItem(this.CART_KEY, JSON.stringify(items));
  }

  getCart() {
    return this.cartItemsSubject.value;
  }

  addItem(item: CartItem) {
    const current = this.getCart();
    const existingIndex = current.findIndex(ci => ci.id === item.id);
    if (existingIndex !== -1) {
      current[existingIndex].quantity += item.quantity;
      current[existingIndex].price = item.price; // Actualiza el precio
    } else {
      current.push(item);
    }
    this.cartItemsSubject.next([...current]);
    this.saveToLocalStorage(current);
  }

  clearCart() {
    this.cartItemsSubject.next([]);
    this.saveToLocalStorage([]);
  }

  removeItem(itemId: string) {
    const current = this.getCart();
    const updated = current.filter(item => item.id !== itemId);
    this.cartItemsSubject.next([...updated]);
    this.saveToLocalStorage(updated);
  }

  cleanCartAfterSave() {
    this.cartItemsSubject.next([]);
    localStorage.removeItem(this.CART_KEY);
  }

  removeLocalStorage() {
    localStorage.removeItem(this.CART_KEY);
  }

  updateItem(updated: CartItem) {
    const current = this.getCart();
    const idx = current.findIndex(ci => ci.id === updated.id);
    if (idx !== -1) {
      current[idx] = { ...updated, price: updated.price };
    } else {
      current.push({ ...updated, price: updated.price });
    }
    this.cartItemsSubject.next([...current]);
    this.saveToLocalStorage(current);
  }

  updateItemById(cartItemId: string, patch: Partial<CartItem>) {
    const current = this.getCart();
    const idx = current.findIndex(ci => ci.id === cartItemId);
    if (idx === -1) return;
    const merged = { ...current[idx], ...patch };
    current[idx] = merged;
    this.cartItemsSubject.next([...current]);
    this.saveToLocalStorage(current);
  }

  refreshCartFromApi(): Observable<void> {
    const current = this.getCart();
    if (!current.length) {
      return of(undefined);
    }

    const allIds = current.map(item => item.id);
    const addressNumber = this.accountService.userIdentity()?.user.addressNumber;

    return this.itemService.find_by_ids(allIds, addressNumber).pipe(
      tap(response => {
        const apiItems = response.body?.item?.itemList ?? [];

        const apiItemMap = new Map(apiItems.map(item => [item.id, item]));

        const updated = current.map(cartItem => {
          const apiItem = apiItemMap.get(cartItem.id);
          let newQuantity = cartItem.quantity;
          let newLeadTime = cartItem.leadTime;

          if (apiItem) {
            const platformCount = cartItem.platformCount ?? 0;
            const halfPlatformCount = cartItem.halfPlatformCount ?? 0;
            const quarterPlatformCount = cartItem.quarterPlatformCount ?? 0;
            const eighthPlatformCount = cartItem.eighthPlatformCount ?? 0;

            const platform = Number(apiItem.platform ?? 0) || 0;
            const halfPlatform = Number(apiItem.halfPlatform ?? 0) || 0;
            const quarterPlatform = Number(apiItem.quarterPlatform ?? 0) || 0;
            const eighthPlatform = Number(apiItem.eighthPlatform ?? 0) || 0;

            newQuantity = (platformCount * platform)
              + (halfPlatformCount * halfPlatform)
              + (quarterPlatformCount * quarterPlatform)
              + (eighthPlatformCount * eighthPlatform);

            newLeadTime = apiItem.leadTime ?? cartItem.leadTime;
          }

          return {
            ...cartItem,
            price: cartItem.price,
            quantity: newQuantity,
            leadTime: newLeadTime
          };
        });

        this.cartItemsSubject.next([...updated]);
        this.saveToLocalStorage(updated);
      }),
      map(() => undefined)
    );
  }

}
